import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import { botService } from '../services/bot'
import { formatPrice, formatNumber, formatAmount, formatPercentage } from '@/utils/formatters'
import ConfirmModal from '@/components/ConfirmModal'

interface GridLevel {
  precio: number
  cantidad: number
  ejecutado: boolean
  estado?: 'ejecutado' | 'pendiente' | 'sin_orden'
  orden_id?: string
}

interface Position {
  id: string
  side: 'buy' | 'sell'
  amount: number
  price: number
  timestamp: string
  profit?: number
}

interface GridConfig {
  precio_superior: number
  precio_inferior: number
  numero_grids: number
  tipo_grid: 'aritmetico' | 'geometrico'
  capital_total: number
}

const GridPage: React.FC = () => {
  const [gridLevels, setGridLevels] = useState<GridLevel[]>([])
  const [positions, setPositions] = useState<Position[]>([])
  const [gridConfig, setGridConfig] = useState<GridConfig | null>(null)
  const [currentPrice, setCurrentPrice] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [reinitializing, setReinitializing] = useState(false)
  const [confirmModal, setConfirmModal] = useState(false)
  const [fueraDeRango, setFueraDeRango] = useState(false)
  const [rangoGrid, setRangoGrid] = useState<{min: number, max: number} | null>(null)

  useEffect(() => {
    loadGridData()
  }, [])

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null
    if (autoRefresh) {
      interval = setInterval(() => {
        loadGridData()
      }, 5000)
    }
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [autoRefresh])

  const loadGridData = async () => {
    try {
      const [levelsData, positionsData, configData, priceData] = await Promise.all([
        botService.getGridLevels(),
        botService.getPositions(),
        botService.getGridConfig(),
        botService.getCurrentPrice()
      ])
      setGridLevels(levelsData.niveles || [])
      setPositions(positionsData.posiciones || [])
      setGridConfig(configData)
      setCurrentPrice(priceData.precio)
      setFueraDeRango(levelsData.fuera_de_rango || false)
      if (levelsData.rango_grid) setRangoGrid(levelsData.rango_grid)
    } catch (error) {
      console.error('Error loading grid data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleReinitializeGrid = async () => {
    setConfirmModal(true)
  }

  const confirmReinitialize = async () => {
    setConfirmModal(false)
    setReinitializing(true)
    try {
      await botService.reinitializeGrid()
      await loadGridData()
      toast.success('Grid reinicializado exitosamente')
    } catch (error) {
      console.error('Error reinitializing grid:', error)
      toast.error('Error al reinicializar el grid')
    } finally {
      setReinitializing(false)
    }
  }

  const calculateGridStats = () => {
    const executed = gridLevels.filter(l => l.estado === 'ejecutado' || l.ejecutado).length
    const pending = gridLevels.filter(l => l.estado === 'pendiente' || (!l.ejecutado && l.estado !== 'sin_orden')).length
    const sinOrden = gridLevels.filter(l => l.estado === 'sin_orden').length
    const executionRate = gridLevels.length > 0 ? (executed / gridLevels.length * 100) : 0
    return { executed, pending, sinOrden, executionRate }
  }

  const stats = calculateGridStats()

  const getPricePosition = (price: number) => {
    if (!gridConfig) return 50
    const range = gridConfig.precio_superior - gridConfig.precio_inferior
    const position = ((price - gridConfig.precio_inferior) / range) * 100
    return Math.max(0, Math.min(100, position))
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <>
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">🔲 Grid Trading - Panel de Monitoreo</h1>
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="autoRefreshGrid"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="autoRefreshGrid" className="ml-2 text-sm text-gray-700">
                  Auto-actualizar (5s)
                </label>
              </div>
              <button
                onClick={loadGridData}
                className="bg-gray-600 text-white px-3 py-2 rounded-md hover:bg-gray-700 text-sm"
              >
                🔄 Actualizar
              </button>
              <button
                onClick={handleReinitializeGrid}
                disabled={reinitializing}
                className="bg-orange-600 text-white px-3 py-2 rounded-md hover:bg-orange-700 text-sm disabled:opacity-50"
              >
                {reinitializing ? '⏳ Reinicializando...' : '🔄 Reinicializar Grid'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Alerta: Grid fuera de rango */}
      {fueraDeRango && currentPrice && rangoGrid && (
        <div className="bg-red-50 border-l-4 border-red-500 rounded-lg shadow p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 text-2xl mr-3">⚠️</div>
            <div>
              <h3 className="text-lg font-bold text-red-800">Grid fuera del rango del mercado</h3>
              <p className="text-red-700 mt-1">
                El precio actual (<strong>{formatPrice(currentPrice, 2)}</strong>) está completamente fuera del rango del grid 
                (<strong>{formatPrice(rangoGrid.min, 2)}</strong> - <strong>{formatPrice(rangoGrid.max, 2)}</strong>).
              </p>
              <p className="text-red-700 mt-1">
                Las órdenes no se ejecutarán porque ningún nivel está cerca del precio de mercado.
                <strong> Debes reconfigurar el grid</strong> con un rango que incluya el precio actual.
              </p>
              <p className="text-red-600 text-sm mt-2">
                💡 Sugerencia: Configura el rango entre <strong>{formatPrice(currentPrice * 0.95, 0)}</strong> y <strong>{formatPrice(currentPrice * 1.05, 0)}</strong> para un grid ±5% alrededor del precio actual.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Configuración del Grid */}
      {gridConfig && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">⚙️ Configuración del Grid</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Precio Superior</p>
                <p className="text-xl font-bold text-blue-600">{formatPrice(gridConfig.precio_superior, 2)}</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Precio Inferior</p>
                <p className="text-xl font-bold text-green-600">{formatPrice(gridConfig.precio_inferior, 2)}</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Número de Grids</p>
                <p className="text-xl font-bold text-purple-600">{gridConfig.numero_grids}</p>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Tipo de Grid</p>
                <p className="text-xl font-bold text-yellow-600 capitalize">{gridConfig.tipo_grid}</p>
              </div>
              <div className="text-center p-4 bg-indigo-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Capital Total</p>
                <p className="text-xl font-bold text-indigo-600">{formatPrice(gridConfig.capital_total, 2)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Estadísticas del Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-xl">🔲</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Niveles</p>
              <p className="text-2xl font-semibold text-gray-900">{gridLevels.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-xl">✅</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Ejecutados</p>
              <p className="text-2xl font-semibold text-green-600">{stats.executed}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                <span className="text-xl">⏱️</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Pendientes</p>
              <p className="text-2xl font-semibold text-yellow-600">{stats.pending}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-xl">📊</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Tasa Ejecución</p>
              <p className="text-2xl font-semibold text-purple-600">{formatPercentage(stats.executionRate, 1)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Visualización del Grid */}
      {gridConfig && currentPrice && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">📊 Visualización del Grid</h2>
          </div>
          <div className="p-6">
            <div className="relative">
              {/* Barra de rango */}
              <div className="h-8 bg-gradient-to-r from-green-200 via-yellow-200 to-red-200 rounded-lg relative">
                {/* Indicador de precio actual */}
                <div
                  className="absolute top-0 bottom-0 w-1 bg-blue-600 transform -translate-x-1/2"
                  style={{ left: `${getPricePosition(currentPrice)}%` }}
                >
                  <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-3 py-1 rounded text-sm font-bold whitespace-nowrap">
                    {formatPrice(currentPrice, 2)}
                  </div>
                </div>
              </div>

              {/* Etiquetas de precio */}
              <div className="flex justify-between mt-2 text-sm font-medium">
                <span className="text-green-600">{formatPrice(gridConfig.precio_inferior, 2)}</span>
                <span className="text-red-600">{formatPrice(gridConfig.precio_superior, 2)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabla de Niveles del Grid */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">🔧 Niveles del Grid</h2>
        </div>
        <div className="p-6">
          {gridLevels.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nivel
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Precio
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cantidad
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Orden ID
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {gridLevels.map((level, index) => (
                    <tr key={index} className={level.estado === 'ejecutado' ? 'bg-green-50' : level.estado === 'sin_orden' ? 'bg-gray-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-600">
                        {formatPrice(level.precio, 4)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatAmount(level.cantidad, 6)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          level.estado === 'ejecutado'
                            ? 'bg-green-100 text-green-800'
                            : level.estado === 'sin_orden'
                            ? 'bg-gray-100 text-gray-600'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {level.estado === 'ejecutado' ? '✅ Ejecutado' 
                            : level.estado === 'sin_orden' ? '⚪ Sin orden'
                            : '⏱️ Pendiente'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                        {level.orden_id || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">🔲</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay niveles de grid</h3>
              <p className="text-gray-500">Configura e inicia el bot para ver los niveles del grid</p>
            </div>
          )}
        </div>
      </div>

      {/* Posiciones Activas */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">📈 Posiciones Activas</h2>
        </div>
        <div className="p-6">
          {positions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cantidad
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Precio
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      P&L
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {positions.map((position) => (
                    <tr key={position.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500">
                        {position.id.substring(0, 8)}...
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          position.side === 'buy' 
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {position.side === 'buy' ? '📈 BUY' : '📉 SELL'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatAmount(position.amount, 6)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-600">
                        {formatPrice(position.price, 4)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {position.profit !== undefined && (
                          <span className={`text-sm font-medium ${
                            position.profit >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {position.profit >= 0 ? '+' : ''}{formatPrice(position.profit, 2)}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(position.timestamp).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">📊</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay posiciones activas</h3>
              <p className="text-gray-500">Las posiciones aparecerán aquí cuando el grid ejecute operaciones</p>
            </div>
          )}
        </div>
      </div>
    </div>

      <ConfirmModal
        isOpen={confirmModal}
        title="Reinicializar Grid"
        message="¿Estás seguro de que quieres reinicializar el grid? Esto cancelará todas las órdenes pendientes."
        confirmText="Reinicializar"
        variant="warning"
        onConfirm={confirmReinitialize}
        onCancel={() => setConfirmModal(false)}
      />
    </>
  )
}

export default GridPage